<template>
  <div class="d-profile__form">
    <div class="d-profile__form__edit"
         v-if="canEdit"
         @click="isEditable = !isEditable">
      <template v-if="!isEditable">{{ $t('vue2.common.edit') }}</template>
      <template v-else>Afslut redigering</template>
    </div>
    <div class="d-profile__form__body">
      <h3>Undervisningskompetencer (Linjefag)</h3>
      <Loader v-if="isLoading" bg-color="#fff"></Loader>
      <p class="text-explainer" v-if="!isLoading && competencies.length < 1">
        Ingen fag er tilføjet som kompetence
      </p>
      <div class="marg-1-top">
        <template v-if="isEditable">
          <div class="div-explainer">
            <h3>L eller K?</h3>
            <p class="text-explainer"><b>(L) Linjefag</b>: At have undervisningskompetence i et fag betyder, at
              underviseren har haft det pågældende fag som linjefag på læreruddannelsen eller som ekstra linjefag på et
              senere tidspunkt.</p>
            <p class="text-explainer"><b>(K) Tilsvarende kompetence</b>: At have kompetence svarende til
              undervisningskompetence betyder, at underviseren for eksempel har en efteruddannelse, videreuddannelse,
              kompetencegivende uddannelse eller et længerevarende kursusforløb, der vurderes at give kompetence
              svarende
              til undervisningskompetence.</p>
          </div>
          <label class="d-label marg-1-top marg-1-bottom">Tilføj op til seks kompetencer</label>
          <DSelect
            :items="standardSubjectsWithVProps"
            :itemsSelected="userCompetencies"
            :show="true"
            placeholder="Tilføj kompetence"
            @update="updateSubjectOnUser($event)">
          </DSelect>
        </template>
        <ul class="linjefag linjefag--not-empty" v-if="!isEditable">
          <li class="linjefag__add" v-if="userCompetencies.length === 0">
            Tilføj op til seks kompetencer
          </li>
          <li class="d-pill" v-for="subject in userCompetencies">
            {{ subject.name }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '../common/Loader'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import DSelect from '../Reporting/KompetenceIndberetning/KompetenceIndberetningDSelect'

export default {
  components: {Loader, DSelect},
  props: {
    user: {
      default: null
    },
    schoolId: {
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      isEditable: false,
      formErrors: {},
      isSaving: false,
      competencies: [],
      theNewSubject: null,
      theNewSubjectLevel: 'L',
      standardSubjects: [
        {subject_code: 110, name: 'Dansk'},
        {subject_code: 120, name: 'Engelsk'},
        {subject_code: 130, name: 'Tysk (tilbudsfag)'},
        {subject_code: 134, name: 'Tysk (valgfag)'},
        {subject_code: 140, name: 'Fransk (tilbudsfag)'},
        {subject_code: 144, name: 'Fransk (valgfag)'},
        {subject_code: 170, name: 'Kristendomskundskab'},
        {subject_code: 171, name: 'Kristendomskundskab/religion'},
        {subject_code: 181, name: 'Historie'},
        {subject_code: 182, name: 'Samfundsfag'},
        {subject_code: 210, name: 'Idræt'},
        {subject_code: 220, name: 'Musik'},
        {subject_code: 230, name: 'Billedkunst'},
        {subject_code: 240, name: 'Håndarbejde'},
        {subject_code: 245, name: 'Håndværk og design'},
        {subject_code: 250, name: 'Sløjd'},
        {subject_code: 260, name: 'Madkundskab'},
        {subject_code: 310, name: 'Matematik'},
        {subject_code: 320, name: 'Fysik/kemi'},
        {subject_code: 330, name: 'Geografi'},
        {subject_code: 340, name: 'Biologi'},
        {subject_code: 350, name: 'Natur/teknik'},
        {subject_code: 511, name: 'Praktisk/Musisk valgfag: Håndværk/design'},
        {subject_code: 520, name: 'Praktisk/Musisk valgfag: Musik'},
        {subject_code: 531, name: 'Praktisk/Musisk valgfag: Billedkunst'},
        {subject_code: 540, name: 'Praktisk/Musisk valgfag: Madkundskab'},
        {subject_code: 600, name: 'Børnehaveklasse'}
      ],
      levelMap: {
        L: 'Linjefag',
        K: 'Tilsvarende kompetencer'
      }
    }
  },
  computed: {
    isSaveButtonDisabled () {
      return false
    },
    standardSubjectsWithVProps () {
      let subjects = []
      const sortedSubjects = this.standardSubjects.sort((a, b) => {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })
      sortedSubjects.forEach(subject => {
        for (let key of Object.keys(this.levelMap)) {
          let obj = {}
          obj.level = key
          obj.name = subject.name + ' (' + key + ')'
          obj.id = subject.subject_code + '_' + key
          obj.user_id = subject.subject_code + '_' + key
          obj.subject_code = subject.subject_code
          subjects.push(obj)
        }
      })
      return subjects
    },
    userCompetencies () {
      const competencies = []
      this.competencies.forEach(subject => {
        const id = subject.subject_code + '_' + subject.level
        const idx = this.standardSubjectsWithVProps.findIndex(subjectWithVProps => subjectWithVProps.id === id)
        const obj = {
          id: subject.subject_code + '_' + subject.level,
          subject_code: subject.subject_code,
          level: subject.level,
          name: idx > -1 ? this.standardSubjectsWithVProps[idx].name : ''
        }
        competencies.push(obj)
      })
      return competencies
    }
  },
  methods: {
    removeErrors (title) {
      this.$delete(this.formErrors, title)
    },
    cancel () {
      this.resetForm()
      this.isEditable = false
    },
    updateSubjectOnUser (subjects) {
      const user = this.user
      const subjectForServer = []
      subjects.forEach(subject => {
        const obj = {
          subject_code: subject.subject_code,
          level: subject.level
        }
        subjectForServer.push(obj)
      })
      docendo.updateUserCompetencies(this.schoolId, user.id, subjectForServer, success => {
        const userCompetencies = success.filter(subject => subject.user_id === this.user.id)
        this.competencies = userCompetencies
        eventbus.toast = {text: 'Linjefag er opdateret', success: true}
      }, failure => {
        console.log('failed')
      })
    },
    readCompetencies () {
      this.isLoading = true
      docendo.listUserCompetencies(this.schoolId, this.user.id, success => {
        const userCompetencies = success.filter(subject => subject.user_id === this.user.id)
        this.competencies = userCompetencies
        this.isLoading = false
      }, failure => {
        console.log('failure')
      })
    }
  },
  mounted () {
    this.readCompetencies()
  }
}
</script>

<style :scoped>
@import "../../assets/css/variables.css";

.d-profile__form {
  position: relative;
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
  background: #fff;
  border-radius: 4px;
  padding-top: 1rem;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.d-profile__form__edit {
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  color: var(--purple);
  font-weight: bold;
  cursor: pointer;
}

.linjefag {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.25rem 0.5rem;
  border: 1px dashed #ccc;
  border-radius: 4px;
  transition: all 0.25s;
}

.d-pill {
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  font-size: 1rem;
  color: #222222;
  display: inline-block;
  border-radius: 4px;
  padding: 4px 8px;
  line-height: 1rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.text-explainer p {
  margin-bottom: 0.5rem;
}

.d-profile__form__body {
  padding: 0 1rem 1rem 1rem;
}

.text-explainer {
  margin-bottom: 0.5rem;
}

.div-explainer {
  padding: 1rem;
  border-radius: 4px;
  background: #edf0f5;
}
</style>



// WEBPACK FOOTER //
// ProfileSubjects.vue?77626556